import {
  alertError,
  alertSuccess,
  createJsonHeaders,
  logoutOnRequestOrResponseJson,
} from '../../../api'
import { BASE_URL, HOME_PATH } from '../../../configs'
import { access } from '../../../security'
import { updateDialogFlowConfigs } from '../actions/activeBot'
import { setActiveBot } from '../actions/activeBot'
import { saveBots } from '../actions/bots'
import { store } from '../../../index'

/**
 * Paginated organisation list (CareBrain / QCS homes).
 *
 * Separate from `getBots`: that still loads the full set into Redux for create
 * name checks, Menu, and `isQcsBot(id)` lookups. This endpoint must not replace
 * that store with a single platform page.
 *
 * Query params sent by the frontend:
 * - page, size, platformFlag, search — documented list contract
 * - sortBy, sortDir — global Organisation / Your access / Tier / Care Plan Audits sort across pages
 *   (sortBy: id | name | role | packageName | carePlanAuditPercentage, sortDir: asc | desc)
 * - role — filter by this admin’s role on the bot
 *   (ROLE_ADMIN | ROLE_CARECO_ADMIN | ROLE_MANAGER; omit = all)
 */
/** UI column key → API `sortBy` value for GET /bot/list. */
export const BOT_LIST_SERVER_SORT = {
  name: 'name',
  // UI "Your access" column key stays access; API sorts by role.
  access: 'role',
  packageName: 'packageName',
  // Usage column UI key stays carePlanAudits (USAGE_METRIC); API sorts by percentage.
  carePlanAudits: 'carePlanAuditPercentage',
}

/** Roles accepted as GET /bot/list?role= (mirrors ROLE_* in security). */
export const BOT_LIST_ROLE_VALUES = {
  ROLE_ADMIN: 'ROLE_ADMIN',
  ROLE_CARECO_ADMIN: 'ROLE_CARECO_ADMIN',
  ROLE_MANAGER: 'ROLE_MANAGER',
}

export function isBotListServerSortKey(sortBy) {
  return Boolean(BOT_LIST_SERVER_SORT[sortBy])
}

/** Map UI roleFilter → API `role`; omit when “all”. */
export function toBotListRole(roleFilter) {
  if (!roleFilter || roleFilter === 'all') return undefined
  return BOT_LIST_ROLE_VALUES[roleFilter] || undefined
}

export function listBots({
  page = 0,
  size = 10,
  platformFlag,
  search = '',
  sortBy,
  sortDir,
  role,
  signal,
} = {}) {
  const params = new URLSearchParams()
  params.set('page', String(page))
  params.set('size', String(size))
  if (platformFlag) params.set('platformFlag', platformFlag)
  const trimmed = String(search || '').trim()
  if (trimmed) params.set('search', trimmed)

  const apiSortBy = BOT_LIST_SERVER_SORT[sortBy]
  if (apiSortBy) {
    params.set('sortBy', apiSortBy)
    params.set('sortDir', sortDir === 'desc' ? 'desc' : 'asc')
  }

  const roleFilter = toBotListRole(role)
  if (roleFilter) params.set('role', roleFilter)

  return fetch(`${BASE_URL}/bot/list?${params.toString()}`, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
    signal,
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      const payload = json?.data || json
      const items = Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload)
          ? payload
          : []
      return {
        items,
        total: typeof payload?.total === 'number' ? payload.total : items.length,
        page: typeof payload?.page === 'number' ? payload.page : page,
        limit: typeof payload?.limit === 'number' ? payload.limit : size,
        hasNext: Boolean(payload?.hasNext),
        // QCS estate aggregates (list root) — optional; CareBrain responses omit them.
        totalOrganizations:
          typeof payload?.totalOrganizations === 'number' ? payload.totalOrganizations : undefined,
        activeOrganizations:
          typeof payload?.activeOrganizations === 'number' ? payload.activeOrganizations : undefined,
        packagesInUse:
          typeof payload?.packagesInUse === 'number' ? payload.packagesInUse : undefined,
      }
    })
    .catch(error => {
      if (error?.name === 'AbortError') throw error
      alertError('Sorry but something is going wrong please ping support!')
      return { items: [], total: 0, page: 0, limit: size, hasNext: false }
    })
}

export function getBots(callback) {
  fetch(BASE_URL + '/bot', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      if (!store.getState().security || !store.getState().security.authenticated) return
      saveBots(json)
    })
    .catch(function(error) {
      alertError('Sorry but something is going wrong please ping support!')
    })
    .finally(() => {
      if (callback) callback()
    })
}

export function getAIModels(callback, errorCallback) {
  fetch(BASE_URL + '/admin/bot-settings/ai-models', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      if (callback) callback(json)
    })
    .catch(function(error) {
      if (errorCallback) errorCallback(error)
    })
}

export function getBot(botId) {
  const state = store.getState()
  if (!state.security || !state.security.authenticated) {
    return Promise.resolve(undefined)
  }
  const requestedAdminId = state.adminUser && state.adminUser.id

  return fetch(BASE_URL + '/bot/' + botId, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      const next = store.getState()
      if (!next.security || !next.security.authenticated) return undefined
      const currentAdminId = next.adminUser && next.adminUser.id
      if (requestedAdminId != null && currentAdminId !== requestedAdminId) return undefined
      setActiveBot(json)
      return json
    })
    .catch(function(error) {
      alertError('Sorry but something is going wrong please ping support!')
      return undefined
    })
}

export function getDashboard(botId, state) {
  fetch(BASE_URL + '/bot/' + botId + '/dashboard', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      state.setState({ ...json })
    })
    .catch(function(error) {
      alertError('Sorry but something is going wrong please ping support!')
    })
}

export function deleteBot(botId) {
  fetch(BASE_URL + '/bot/' + botId, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'DELETE',
  })
    .then(response => {
      window.location.href = HOME_PATH
      return response
    })
    .catch(function(error) {
      alertError('Fail. Try it later')
    })
}

export function createNewBotAPI(bot, callback, callbackError) {
  fetch(BASE_URL + '/bot', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(bot),
  })
    .then(response => {
      if (response.redirected) {
        window.location.href = response.url
        return null
      }
      if (response.ok) {
        return logoutOnRequestOrResponseJson(response).then(json => {
          alertSuccess('Bot is created successfully.')
          access.loadRole()
          const created = json?.data && typeof json.data === 'object' ? json.data : json
          return new Promise(resolve => {
            getBots(() => resolve(created && created.id != null ? created : json))
          })
        })
      }
      if (response.status === 400 || response.status === 500) {
        return response.json().then(text => {
          if (callbackError) callbackError(text.message)
          return false
        })
      }
      return logoutOnRequestOrResponseJson(response).then(() => false)
    })
    .then(created => {
      if (created && callback) callback(created === true ? undefined : created)
    })
    .catch(error => {
      if (callbackError) {
        callbackError(error?.message || 'Fail. Try it later')
      } else {
        alertError(error?.message || 'Fail. Try it later')
      }
    })
}

export function shareBot(botId, json, callback) {
  fetch(BASE_URL + '/bot/' + botId + '/shareBot', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(json),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(() => {
      if (callback) callback()
      alertSuccess('Bot is shared successfully.')
    })
    .catch(error => alertError(error?.message || 'Fail. Try it later'))
}

export function cloneBot(botId, json, callback) {
  fetch(BASE_URL + '/bot/' + botId + '/clone', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(json),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(({ status, body }) => {
      if (status >= 400) {
        alertError('Fail. Try it later')
        throw new Error(body?.error || 'Unknown error')
      }

      alertSuccess('Bot is cloned successfully.')
      access.loadRole()
      getBots()
      if (callback) {
        callback()
      }
    })
    .catch(function(error) {
      const errorMessage = error.message || ''
      if (errorMessage.includes('Can\'t clone')) {
        alertError("Can't clone bot with patient-related documents")
      } else {
        alertError('Fail. Try again later.')
      }

    })
}

export function updateBot(bot, callback) {
  fetch(BASE_URL + '/bot', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'PUT',
    body: JSON.stringify(bot),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      setActiveBot(json)
      if (callback) callback()
      alertSuccess('Bot is updated successfully.')
    })
    .catch(function(error) {
      alertError('Fail. Try it later')
    })
}

export function getBotPersistentMenu(botId, language, callback) {
  fetch(BASE_URL + '/bot/' + botId + '/persistentMenu?language=' + language, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'GET',
  })
    .then(response => {
      return response.redirected
        ? (window.location.href = response.url)
        : response.json()
    })
    .then(json => {
      if (callback) callback(json)
    })
    .catch(function(error) {
      alertError('Failed to get the persistent menu.')
    })
}

export function updateBotPersistentMenu(botId, language, persistentMenu) {
  persistentMenu.locale = 'default' //todo check logic on persistent component
  fetch(BASE_URL + '/bot/' + botId + '/persistentMenu?language=' + language, {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'PUT',
    body: JSON.stringify(persistentMenu),
  })
    .then(response => logoutOnRequestOrResponseJson(response))
    .then(json => {
      alertSuccess('Bot is updated successfully.')
      setActiveBot(json)
    })
    .catch(function(error) {
      alertError('Fail. Try it later')
    })
}

export function updateDialogflowConfigs(
  botId,
  dialogFlowConfigs,
  successCallback,
  errorCallback,
) {
  fetch(BASE_URL + '/bot/' + botId + '/updateDialogflowConfigs', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'PATCH',
    body: JSON.stringify(dialogFlowConfigs),
  })
    .then(response => {
      if (response.ok) {
        response.redirected
          ? (window.location.href = response.url)
          : alertSuccess('Bot is updated successfully.')
        updateDialogFlowConfigs(dialogFlowConfigs)
        if (successCallback) successCallback()
      } else if (errorCallback) {
        response.json().then(err => errorCallback(err.message))
      }
    })
    .catch(function(error) {
      alertError('Fail. Try it later')
    })
}

export function updateWitAiConfigsRequest(
  botId,
  successCallback,
  errorCallback,
) {
  fetch(BASE_URL + '/bot/' + botId + '/witAiConfigs', {
    credentials: 'include',
    headers: createJsonHeaders(),
    method: 'POST',
  })
    .then(response => {
      if (response.ok) {
        response.redirected
          ? (window.location.href = response.url)
          : alertSuccess('Bot is updated successfully.')
        if (successCallback) successCallback()
      } else if (errorCallback) {
        response.json().then(err => errorCallback(err.message))
      }
    })
    .catch(function(error) {
      alertError('Fail. Try it later')
    })
}
